import { useCallback, useState, useRef, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import noticiaService from 'services/noticiaService';
import { INoticia,ICategory } from 'types/noticia';

const ITEMS_PER_PAGE = 6;

export function useNoticiasBusca() {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  const [noticias, setNoticias] = useState<INoticia[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingCategoria, setLoadingCategoria] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [inputBusca, setInputBusca] = useState<string>('');
  const [busca, setBusca] = useState<string>('');
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<string>('');
  
  const lastNewsRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef<boolean>(false);
  const observer = useRef<IntersectionObserver | null>(null);
  
  // Reset News state when changing filters
  const resetNewsState = useCallback(() => {
    setNoticias([]);
    setHasNextPage(false);
    setPage(1); // Reset page to 1
  }, []);

  // Initialize from URL params
  useEffect(() => {
    const categoriaParam = searchParams?.get('categoria') || '';
    const buscaParam = searchParams?.get('busca') || '';
    
    if (categoriaParam) setCategoriaSelecionada(categoriaParam);
    if (buscaParam) {
      setInputBusca(buscaParam);
      setBusca(buscaParam);
    }
    
    // Initial load - delay slightly to ensure state is set
    setTimeout(() => {
      fetchNoticias(buscaParam, categoriaParam, true);
    }, 0);
  }, [searchParams]);

  const fetchNoticias = useCallback(
    async (customBusca?: string, customCategoria?: string, reset = false) => {
      if (loadingMoreRef.current || (loading && !reset && page > 1)) return;
  
      loadingMoreRef.current = true;
      setLoading(true);
  
      try {
        // IMPORTANT CHANGE: Always use page 1 for reset operations
        const currentPage = reset ? 1 : page;
        const searchBusca = customBusca !== undefined ? customBusca : busca;
        const searchCategoria = customCategoria !== undefined ? customCategoria : categoriaSelecionada;
        
        const result = await noticiaService.fetchNoticias({
          busca: searchBusca,
          categoria: searchCategoria,
          page: currentPage,
          limit: ITEMS_PER_PAGE
        });
        
        const newNoticias = result.noticias;
  
        // If reset and no Newss returned, stop trying to load
        if (reset && newNoticias.length === 0) {
          setNoticias([]);
          setHasNextPage(false);
          setFirstLoad(false);
          return; 
        }
  
        if (reset) {
          setNoticias(newNoticias);
          // IMPORTANT: Always update the page to 2 after a reset fetch that found Newss
          if (result.hasNextPage) {
            setPage(2);
          }
        } else {
          setNoticias((prevNoticias: INoticia[]) => {
            const allNoticias = [...prevNoticias, ...newNoticias];
            return Array.from(
              new Map(allNoticias.map(noticia => [noticia._id, noticia])).values()
            ) as INoticia[];
          });
          // Only increment page when not resetting
          if (result.hasNextPage) {
            setPage(prevPage => prevPage + 1);
          }
        }
        
        setHasNextPage(result.hasNextPage && newNoticias.length > 0);
        setFirstLoad(false);
      } catch (err) {
        console.error('Erro ao buscar noticias:', err);
      } finally {
        setLoading(false);
        loadingMoreRef.current = false;
        setLoadingCategoria(false);
      }
    },
    [page, loading, busca, categoriaSelecionada]
  );

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    if (loading || !hasNextPage) return;

    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage && !loading && !loadingMoreRef.current) {
        fetchNoticias(undefined, undefined);
      }
    }, { threshold: 0.5 });
    
    if (lastNewsRef.current) {
      observer.current.observe(lastNewsRef.current);
    }
    
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [lastNewsRef, loading, hasNextPage, fetchNoticias]);

  // Handle search execution
  const handleExecutarBusca = useCallback(() => {
    if (inputBusca === busca && !categoriaSelecionada) return;
    
    // Update URL with search params
    const params = new URLSearchParams();
    if (inputBusca) params.set('busca', inputBusca);
    if (categoriaSelecionada) params.set('categoria', categoriaSelecionada);
    
    const queryString = params.toString();
    router.push(`/noticias${queryString ? `?${queryString}` : ''}`);
    
    setBusca(inputBusca);
    resetNewsState();
    
    // Important: Always fetch with reset=true to start from page 1
    fetchNoticias(inputBusca, categoriaSelecionada, true);
  }, [inputBusca, busca, categoriaSelecionada, router, resetNewsState, fetchNoticias]);

  const handleCategoriaClick = useCallback((categoriaSlug: string) => {
    // If the category is already selected, do nothing
    if (categoriaSlug === categoriaSelecionada) return;
    
    // Update URL with category param
    const params = new URLSearchParams();
    if (categoriaSlug) params.set('categoria', categoriaSlug);
    if (busca) params.set('busca', busca);
    
    const queryString = params.toString();
    router.push(`/noticias${queryString ? `?${queryString}` : ''}`);
    
    setCategoriaSelecionada(categoriaSlug);
    resetNewsState();
    setLoadingCategoria(true);
    
    // IMPORTANT: Always fetch with reset=true to ensure page 1 is loaded
    fetchNoticias(busca, categoriaSlug, true);
  }, [categoriaSelecionada, busca, router, resetNewsState, fetchNoticias]);

  // Handle clearing filters
  const handleLimparFiltro = useCallback(() => {
    if (!categoriaSelecionada && !busca) return;
    
    router.push('/noticias');
    setCategoriaSelecionada('');
    setInputBusca('');
    setBusca('');
    resetNewsState();
    
    // IMPORTANT: Always fetch with reset=true
    fetchNoticias('', '', true);
  }, [categoriaSelecionada, busca, router, resetNewsState, fetchNoticias]);

  return {
    inputBusca,
    setInputBusca,
    busca,
    categoriaSelecionada,
    noticias,
    loading,
    loadingCategoria,
    hasNextPage,
    firstLoad,
    lastNewsRef,
    handleExecutarBusca,
    handleLimparFiltro,
    handleCategoriaClick
  };
}

/**
 * Hook para gerenciar as categorias de noticias
 * @returns {Object} - Estados e funções para gerenciar categorias
 */
export const useCategorias = () => {
  const [categorias, setCategorias] = useState<ICategory[]>([]);
  const [loadingCategorias, setLoadingCategorias] = useState<boolean>(true);

  useEffect(() => {
    const loadCategorias = async () => {
      try {
        setLoadingCategorias(true);
        const categoriasData = await noticiaService.fetchCategorias();
        setCategorias(categoriasData);
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
      } finally {
        setLoadingCategorias(false);
      }
    };

    loadCategorias();
  }, []);

  return {
    categorias,
    loadingCategorias
  };
};

let cacheNoticias: INoticia[] | null = null;

export const useNoticiasSwiffy = (limit?: number) => {
  const [noticias, setNoticias] = useState<INoticia[]>(cacheNoticias || []);
  const [isLoading, setIsLoading] = useState(!cacheNoticias);

  useEffect(() => {
    if (cacheNoticias) {
      setIsLoading(false);
      // Aplicar limite se fornecido
      if (limit) {
        setNoticias(cacheNoticias.slice(0, limit));
      } else {
        setNoticias(cacheNoticias);
      }
      return;
    }

    const fetchNoticias = async () => {
      try {
        const data = await noticiaService.fetchFeaturedNoticias(limit);
        const arr = Array.isArray(data) ? data : [];
        cacheNoticias = arr;
        setNoticias(arr);
      } catch (error) {
        console.error('Erro ao buscar notícias:', error);
        setNoticias([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNoticias();
  }, [limit]);

  return { noticias, isLoading };
};
